/* Module Technique — refonte selon la maquette officielle SESAM.
 * 5 KPI + liste interventions avec technicien (avatar + tel) + statut + Effectué
 * + panneau latéral de détails (photos + commentaire + signature + actions).
 */
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  Calendar, CheckCircle2, Clock, X, Printer, ChevronLeft, ChevronRight,
  Wrench, RefreshCw, MapPin, User, MoreHorizontal, Camera, Trash2, PenLine, ImageIcon,
  ClipboardList, Settings, Monitor, FileText, Zap, Plus, Search, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { EnvoisAffichesPanel } from '../components/EnvoisAffichesPanel';

const API = process.env.REACT_APP_BACKEND_URL;

// Icône par type d'intervention
const TYPE_ICON = {
  'Entretien': Wrench,
  'Changement toile': RefreshCw,
  'Réparation': Wrench,
  'Modification': RefreshCw,
  'Changement d\'affiche': RefreshCw,
  'Mise à jour': RefreshCw,
};

const TYPE_COLOR = {
  'Entretien': 'text-blue-600',
  'Changement toile': 'text-emerald-600',
  'Changement d\'affiche': 'text-emerald-600',
  'Réparation': 'text-orange-600',
  'Modification': 'text-violet-600',
  'Mise à jour': 'text-violet-600',
};

const STATUT_STYLE = {
  'Prévu':    { bg: 'text-amber-700', dot: 'bg-amber-500', label: 'En attente' },
  'En attente':{ bg: 'text-amber-700', dot: 'bg-amber-500', label: 'En attente' },
  'En cours': { bg: 'text-blue-700', dot: 'bg-blue-500', label: 'En cours' },
  'En attente validation': { bg: 'text-purple-700', dot: 'bg-purple-500', label: 'À valider' },
  'Effectué': { bg: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Effectué' },
  'Annulé':   { bg: 'text-rose-700', dot: 'bg-rose-500', label: 'Annulé' },
};

function todayISO() { return new Date().toISOString().slice(0, 10); }
function formatDateFR(iso) { if (!iso) return ''; const d = new Date(iso); return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); }
function isoAdd(iso, days) { const d = new Date(iso); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); }

// Avatar coloré généré depuis le nom (initiales + couleur stable)
function Avatar({ name, size = 40 }) {
  const initials = (name || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';
  const colors = ['bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-orange-500', 'bg-rose-500', 'bg-cyan-500'];
  let hash = 0;
  for (const ch of name || 'x') hash = (hash * 31 + ch.charCodeAt(0)) % colors.length;
  return (
    <div className={`${colors[hash]} text-white font-bold rounded-full flex items-center justify-center flex-shrink-0`} style={{ width: size, height: size, fontSize: size * 0.35 }}>
      {initials}
    </div>
  );
}

// Pastille numérotée pour un emplacement (couleur selon type panneau)
function EmplacementBadge({ number, type }) {
  const s = (type || '').toLowerCase();
  const bg = s.includes('led') ? '#F59E0B' : s.includes('grand') ? '#8B5CF6' : '#22C55E';
  return (
    <div className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0" style={{ background: bg }}>
      {number || '·'}
    </div>
  );
}

function KpiCard({ icon: Icon, color, value, label, testid }) {
  return (
    <Card className="border-0 shadow-sm" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="text-2xl font-extrabold text-slate-900 leading-tight">{value}</div>
            <div className="text-[13px] text-slate-600 leading-snug">{label}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function KpiCardV2({ icon: Icon, color, value, label, danger }) {
  return (
    <Card className="border border-slate-200 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className={`text-2xl font-bold leading-tight ${danger ? 'text-red-600' : 'text-slate-900'}`}>{value}</div>
            <div className="text-xs text-slate-500 leading-snug">{label}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Technique({ user, onLogout }) {
  const [interventions, setInterventions] = useState([]);
  const [emplacements, setEmplacements] = useState([]);
  const [date, setDate] = useState(todayISO());
  const [statutFilter, setStatutFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  // Nouveaux : programmer intervention + tâches auto
  const [showProgramSheet, setShowProgramSheet] = useState(false);
  const [showAutoDialog, setShowAutoDialog] = useState(false);
  // Phase 2 : Réaliser la tâche (technicien) + Valider (admin)
  const [realizeTask, setRealizeTask] = useState(null); // intervention en cours de réalisation
  const [validateTask, setValidateTask] = useState(null); // intervention à valider par admin
  const [reportTask, setReportTask] = useState(null);   // intervention à reporter
  const isTechnicien = (user?.role || '').toLowerCase() === 'technicien';

  const loadAll = async () => {
    try {
      const [iRes, eRes] = await Promise.all([
        axios.get(`${API}/api/technique/interventions`, {
          params: { date_debut: date, date_fin: date, ...(statutFilter !== 'all' ? { statut: statutFilter } : {}) },
        }),
        axios.get(`${API}/api/emplacements`).catch(() => ({ data: [] })),
      ]);
      const list = iRes.data?.interventions || [];
      setInterventions(list);
      setEmplacements(eRes.data || []);
      // Ne pas ouvrir automatiquement le panneau — l'utilisateur clique sur un RDV pour l'ouvrir.
    } catch (e) {
      toast.error('Erreur de chargement des interventions');
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [date, statutFilter]);

  // Calcul KPIs (le total est basé sur toutes les interventions du jour, indépendamment du filtre)
  const [allToday, setAllToday] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/technique/interventions`, { params: { date_debut: date, date_fin: date } })
      .then((r) => setAllToday(r.data?.interventions || []))
      .catch(() => setAllToday([]));
  }, [date]);

  const kpis = useMemo(() => ({
    rdv_aujourdhui: allToday.length,
    effectues: allToday.filter(i => i.statut_intervention === 'Effectué').length,
    en_cours: allToday.filter(i => i.statut_intervention === 'En cours').length,
    annules: allToday.filter(i => i.statut_intervention === 'Annulé').length,
  }), [allToday]);

  // Retrouver le numéro d'emplacement à partir du nom du lieu
  const empByName = useMemo(() => {
    const map = new Map();
    for (const e of emplacements) map.set(e.nom, e);
    return map;
  }, [emplacements]);

  // Ordonner par heure croissante
  const rows = useMemo(() => {
    return interventions.slice().sort((a, b) => (a.heure || '').localeCompare(b.heure || ''));
  }, [interventions]);

  const selected = selectedId ? (interventions.find(i => i.id === selectedId) || null) : null;
  const selectedEmp = selected ? empByName.get(selected.lieu) : null;

  const toggleDone = async (intervention) => {
    const isDone = intervention.statut_intervention === 'Effectué';
    try {
      if (isDone) {
        await axios.post(`${API}/api/technique/interventions/${intervention.id}/statut`, { statut: 'Prévu' });
      } else {
        await axios.post(`${API}/api/technique/interventions/${intervention.id}/marquer-effectue`, {});
      }
      toast.success(isDone ? 'Marquée comme non effectuée' : 'Intervention marquée effectuée');
      loadAll();
    } catch { toast.error('Erreur de mise à jour du statut'); }
  };

  const printTournee = () => {
    // Impression du tableau du jour via window.print (uniquement la zone du tableau)
    window.print();
  };

  const saveNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    // Note générale : on la stocke dans une collection dédiée (fallback : localStorage)
    try {
      await axios.post(`${API}/api/technique/notes`, { date, texte: note });
      toast.success('Note enregistrée');
      setNote('');
    } catch {
      // Fallback local
      const key = `sesam-tech-notes-${date}`;
      const prev = JSON.parse(localStorage.getItem(key) || '[]');
      prev.push({ ts: Date.now(), texte: note });
      localStorage.setItem(key, JSON.stringify(prev));
      toast.success('Note enregistrée (local)');
      setNote('');
    } finally { setSavingNote(false); }
  };

  // Tabs (Toutes / À faire / En cours / Terminées / Automatiques)
  const [activeTab, setActiveTab] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());

  // KPIs calculées sur TOUTES les interventions (pas seulement du jour)
  const [allInterventions, setAllInterventions] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/technique/interventions`).then(r => {
      setAllInterventions(r.data?.interventions || []);
    }).catch(() => setAllInterventions([]));
  }, [interventions.length]);

  const kpisMockup = useMemo(() => {
    const list = allInterventions;
    return {
      aFaire:    list.filter(i => i.statut_intervention === 'Prévu' && !i.is_auto).length,
      terminees: list.filter(i => i.statut_intervention === 'Effectué').length,
      enRetard:  list.filter(i => i.en_retard).length,
      auto:      list.filter(i => i.is_auto).length,
    };
  }, [allInterventions]);

  const tabRows = useMemo(() => {
    let list = allInterventions;
    if (activeTab === 'todo')    list = list.filter(i => i.statut_intervention === 'Prévu' && !i.is_auto);
    if (activeTab === 'inprog')  list = list.filter(i => i.statut_intervention === 'En cours');
    if (activeTab === 'done')    list = list.filter(i => i.statut_intervention === 'Effectué');
    if (activeTab === 'auto')    list = list.filter(i => i.is_auto);
    return list;
  }, [allInterventions, activeTab]);

  const printSelection = () => {
    if (selectedIds.size === 0) {
      toast.error('Sélectionnez au moins une tâche à imprimer');
      return;
    }
    const list = tabRows.filter(i => selectedIds.has(i.id));
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Ordre d'intervention</title>
      <style>body{font-family:Arial;margin:24px}h1{color:#1f2937}table{width:100%;border-collapse:collapse;margin-top:12px}th,td{border:1px solid #cbd5e1;padding:8px;font-size:12px;text-align:left}th{background:#f8fafc}.p-h{background:#fee2e2;color:#b91c1c;padding:2px 8px;border-radius:6px;font-size:11px}.p-m{background:#fef3c7;color:#a16207;padding:2px 8px;border-radius:6px;font-size:11px}.p-b{background:#dcfce7;color:#166534;padding:2px 8px;border-radius:6px;font-size:11px}</style></head>
      <body>
      <div style="display:flex;justify-content:space-between;align-items:start">
        <div><h1>🔧 SESAM — Feuille d'intervention</h1>
        <p><b>Technicien :</b> ${list[0]?.technicien || '—'}<br/><b>Date d'édition :</b> ${new Date().toLocaleDateString('fr-FR')}<br/><b>Préparé par :</b> ${user?.name || user?.email || 'Admin'}</p></div>
        <div style="font-size:11px;text-align:right;color:#64748b">${list.length} tâche(s)</div>
      </div>
      <table><thead><tr><th>N°</th><th>Tâche</th><th>Emplacement</th><th>Priorité</th><th>Échéance</th><th>✓</th></tr></thead>
      <tbody>${list.map((i, idx) => `<tr>
        <td>${idx + 1}</td><td>${i.motif || '—'}</td><td>${i.lieu || '—'}</td>
        <td><span class="${i.priorite === 'Haute' ? 'p-h' : i.priorite === 'Basse' ? 'p-b' : 'p-m'}">${i.priorite || 'Moyenne'}</span></td>
        <td>${i.echeance || i.date || '—'}</td><td>□</td></tr>`).join('')}</tbody></table>
      <p style="margin-top:20px;font-size:11px;color:#64748b"><b>💡 Bonne intervention !</b> — Cochez chaque case dès qu'une tâche est terminée, prenez une photo par tâche, puis remontez la fiche à l'admin.</p>
      </body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html); w.document.close();
    setTimeout(() => w.print(), 400);
  };

  // ═══════ ID lisible + badge couleur par tâche ═══════
  const intCode = (i, idx) => {
    const year = (i.date || '').slice(0, 4) || new Date().getFullYear();
    const short = (i.id || '').replace(/-/g, '').slice(0, 4).toUpperCase();
    return `INT-${year}-${String((idx || 0) + 1).padStart(4, '0').slice(-4)}`.replace('0001', short || '0001').slice(0, 14) + '-' + short;
  };
  const TACHE_BADGE = {
    'Maintenance':               'bg-sky-100 text-sky-700',
    'Changement de toile':       'bg-rose-100 text-rose-700',
    "Changement d'affiche":      'bg-violet-100 text-violet-700',
    "Changement d'image":        'bg-amber-100 text-amber-700',
    'Nettoyage':                 'bg-emerald-100 text-emerald-700',
    'Mise à jour des logiciels': 'bg-fuchsia-100 text-fuchsia-700',
    'Contrôle / Inspection':     'bg-indigo-100 text-indigo-700',
    'Réparation':                'bg-orange-100 text-orange-700',
  };
  const tacheBadgeClass = (m) => TACHE_BADGE[m] || 'bg-slate-100 text-slate-600';

  // ═══════ KPIs "vue admin" façon maquette ═══════
  const kpisAdmin = useMemo(() => {
    const today = allToday;
    return {
      total:      today.length,
      enCours:    today.filter(i => i.statut_intervention === 'En cours').length,
      terminees:  today.filter(i => i.statut_intervention === 'Effectué').length,
      aVenir:     today.filter(i => i.statut_intervention === 'Prévu').length,
    };
  }, [allToday]);

  const [typeTacheFilter, setTypeTacheFilter] = useState('all');
  const [search, setSearch] = useState('');

  // ═══════ Rows filtrés admin : jour sélectionné + filtres ═══════
  const adminRows = useMemo(() => {
    let list = allToday.slice();
    if (statutFilter !== 'all') list = list.filter(i => i.statut_intervention === statutFilter);
    if (typeTacheFilter !== 'all') list = list.filter(i => (i.motif || '').toLowerCase().includes(typeTacheFilter.toLowerCase()));
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(i => `${i.motif} ${i.lieu} ${i.panneau?.numero} ${i.technicien}`.toLowerCase().includes(s));
    }
    return list.sort((a, b) => (a.heure || '').localeCompare(b.heure || ''));
  }, [allToday, statutFilter, typeTacheFilter, search]);

  const prochaineIntervention = useMemo(() => {
    const now = new Date();
    const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    return allToday
      .filter(i => i.statut_intervention !== 'Effectué')
      .sort((a, b) => (a.heure || '').localeCompare(b.heure || ''))
      .find(i => (i.heure || '99:99') >= hhmm) || null;
  }, [allToday]);

  const syncNow = () => { loadAll(); toast.success('Synchronisé ✓'); };

  // ═══════ Vue Technicien (mobile-first, 3 tabs) ═══════
  const [techTab, setTechTab] = useState('todo');
  const techCounts = useMemo(() => ({
    todo:   allInterventions.filter(i => i.statut_intervention === 'Prévu').length,
    inprog: allInterventions.filter(i => i.statut_intervention === 'En cours').length,
    done:   allInterventions.filter(i => i.statut_intervention === 'Effectué').length,
  }), [allInterventions]);
  const techRows = useMemo(() => {
    const map = { todo: 'Prévu', inprog: 'En cours', done: 'Effectué' };
    return allInterventions
      .filter(i => i.statut_intervention === map[techTab])
      .sort((a, b) => (a.date || '').localeCompare(b.date || '') || (a.heure || '').localeCompare(b.heure || ''));
  }, [allInterventions, techTab]);
  const openGoogleMapsRoute = () => {
    const stops = allInterventions
      .filter(i => i.statut_intervention !== 'Effectué')
      .map(i => empByName.get(i.lieu))
      .filter(e => e && e.latitude && e.longitude);
    if (stops.length === 0) {
      toast.info('Aucun emplacement géolocalisé pour tracer l\'itinéraire');
      return;
    }
    const waypoints = stops.slice(0, -1).map(e => `${e.latitude},${e.longitude}`).join('|');
    const last = stops[stops.length - 1];
    const origin = stops[0];
    const url = `https://www.google.com/maps/dir/?api=1&origin=${origin.latitude},${origin.longitude}&destination=${last.latitude},${last.longitude}${waypoints ? `&waypoints=${waypoints}` : ''}&travelmode=driving`;
    window.open(url, '_blank');
  };

  // ─────────────────────────────────────────────
  // VUE TECHNICIEN (image 2 : mobile-first)
  // ─────────────────────────────────────────────
  const techView = (
    <div className="max-w-2xl mx-auto space-y-4 pb-24" data-testid="technique-root-tech">
      <div className="flex items-center justify-between px-1">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Mes interventions</h1>
          <p className="text-xs text-slate-500 mt-0.5">Voici vos tâches à réaliser sur le terrain</p>
        </div>
        <Button variant="outline" size="sm" onClick={syncNow} className="gap-1.5 h-9" data-testid="btn-tech-sync">
          <RefreshCw className="h-3.5 w-3.5" /> Sync
        </Button>
      </div>

      <EnvoisAffichesPanel userEmail={user?.email} />

      {/* Tabs pill-shaped */}
      <div className="flex gap-2 bg-slate-100 p-1.5 rounded-full">
        {[
          { id: 'todo',   label: 'À faire',    count: techCounts.todo },
          { id: 'inprog', label: 'En cours',   count: techCounts.inprog },
          { id: 'done',   label: 'Terminées',  count: techCounts.done },
        ].map(t => (
          <button key={t.id}
            onClick={() => setTechTab(t.id)}
            className={`flex-1 h-10 rounded-full text-sm font-semibold transition ${
              techTab === t.id ? 'bg-blue-600 text-white shadow' : 'text-slate-700 hover:bg-white'
            }`}
            data-testid={`tech-tab-${t.id}`}
          >
            {t.label} ({t.count})
          </button>
        ))}
      </div>

      {/* List rows */}
      <Card className="border border-slate-200 shadow-sm">
        <div className="divide-y divide-slate-100">
          {techRows.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-400">Aucune tâche dans cet onglet.</div>
          )}
          {techRows.map((i) => (
            <div key={i.id}
              className="flex items-start gap-3 p-4 hover:bg-slate-50 cursor-pointer"
              onClick={() => setSelectedId(i.id)}
              data-testid={`tech-row-${i.id}`}
            >
              <MapPin className="h-5 w-5 text-slate-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0 space-y-1">
                <div className="font-bold text-slate-900 truncate">{i.lieu || '—'}</div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <Monitor className="h-3.5 w-3.5" />
                  Panneau {i.panneau?.numero ? `#${i.panneau.numero}` : '—'}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <Calendar className="h-3.5 w-3.5" />
                  {i.date ? new Date(i.date).toLocaleDateString('fr-FR') : '—'} {i.heure && `· ${i.heure}`}
                </div>
                <span className={`inline-block mt-1 text-[11px] font-semibold px-2.5 py-1 rounded-full ${tacheBadgeClass(i.motif)}`}>
                  {i.motif || '—'}
                </span>
                {/* Bouton "Effectuer" — visible uniquement pour tâches À faire (Prévu) */}
                {i.statut_intervention === 'Prévu' && (
                  <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" onClick={() => setRealizeTask(i)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 h-9 px-4"
                      data-testid={`tech-btn-effectuer-${i.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> Effectuer
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setReportTask(i)}
                      className="border-red-200 text-red-700 hover:bg-red-50 gap-1.5 h-9 px-3"
                      data-testid={`tech-btn-reporter-${i.id}`}>
                      <X className="h-3.5 w-3.5" /> Reporter
                    </Button>
                  </div>
                )}
                {i.statut_intervention === 'En attente validation' && (
                  <div className="inline-flex items-center gap-1 text-[11px] font-semibold text-purple-700 bg-purple-50 px-2 py-1 rounded-full mt-2">
                    <Clock className="h-3 w-3" /> En attente de validation admin
                  </div>
                )}
              </div>
              <ChevronRight className="h-4 w-4 text-slate-400 mt-2 flex-shrink-0" />
            </div>
          ))}
        </div>
      </Card>

      {/* Sticky bottom bar */}
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-8 md:w-[520px] z-30 flex gap-2 bg-white/95 backdrop-blur border border-slate-200 rounded-2xl p-2 shadow-xl">
        <Button variant="outline" onClick={() => window.print()} className="flex-1 gap-2 h-11" data-testid="btn-tech-print">
          <Printer className="h-4 w-4" /> Imprimer la tournée
        </Button>
        <Button onClick={openGoogleMapsRoute} className="flex-1 bg-blue-600 hover:bg-blue-700 gap-2 h-11" data-testid="btn-tech-itineraire">
          <MapPin className="h-4 w-4" /> Itinéraire
        </Button>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────
  // VUE ADMIN (image 1 : desktop riche)
  // ─────────────────────────────────────────────
  const adminView = (
    <div className="space-y-5" data-testid="technique-root">
      {/* Header title */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Mes interventions</h1>
          <p className="text-sm text-slate-500 mt-1">Voici vos tâches à réaliser sur le terrain</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={() => setShowProgramSheet(true)} className="bg-blue-600 hover:bg-blue-700 gap-2 h-10" data-testid="btn-programmer-tache">
            <Plus className="h-4 w-4" /> Nouvelle intervention
          </Button>
          <Button variant="ghost" onClick={() => setShowAutoDialog(true)} className="gap-2 h-10" data-testid="btn-taches-auto">
            <Settings className="h-4 w-4" /> Tâches automatiques
          </Button>
        </div>
      </div>

      <EnvoisAffichesPanel userEmail={user?.email} />

      {/* ══════ 4 KPIs (mockup image 1) ══════ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0"><ClipboardList className="h-6 w-6 text-blue-600" /></div>
          <div><div className="text-3xl font-extrabold text-slate-900 leading-none">{kpisAdmin.total}</div><div className="text-[13px] text-slate-500 mt-1">interventions aujourd'hui</div></div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0"><Clock className="h-6 w-6 text-sky-600" /></div>
          <div><div className="text-3xl font-extrabold text-slate-900 leading-none">{kpisAdmin.enCours}</div><div className="text-[13px] text-slate-500 mt-1">en cours</div></div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0"><CheckCircle2 className="h-6 w-6 text-emerald-600" /></div>
          <div><div className="text-3xl font-extrabold text-slate-900 leading-none">{kpisAdmin.terminees}</div><div className="text-[13px] text-slate-500 mt-1">terminées</div></div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0"><Clock className="h-6 w-6 text-slate-500" /></div>
          <div><div className="text-3xl font-extrabold text-slate-900 leading-none">{kpisAdmin.aVenir}</div><div className="text-[13px] text-slate-500 mt-1">à venir</div></div>
        </div>
      </div>

      {/* ══════ Grid principal : liste + sidebar ══════ */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        <Card className="border border-slate-200 shadow-sm">
          {/* Filtres */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 p-4 border-b border-slate-100">
            <div className="relative">
              <Calendar className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="pl-9 h-10 bg-slate-50 border-slate-200" data-testid="filter-date" />
            </div>
            <Select value={statutFilter} onValueChange={setStatutFilter}>
              <SelectTrigger className="h-10 bg-slate-50 border-slate-200" data-testid="filter-statut"><SelectValue placeholder="Tous les statuts" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="Prévu">À faire</SelectItem>
                <SelectItem value="En cours">En cours</SelectItem>
                <SelectItem value="Effectué">Terminées</SelectItem>
                <SelectItem value="En attente validation">À valider</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeTacheFilter} onValueChange={setTypeTacheFilter}>
              <SelectTrigger className="h-10 bg-slate-50 border-slate-200" data-testid="filter-type"><SelectValue placeholder="Tous les types de tâches" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types de tâches</SelectItem>
                <SelectItem value="Maintenance">Maintenance</SelectItem>
                <SelectItem value="Changement de toile">Changement de toile</SelectItem>
                <SelectItem value="Changement d'affiche">Changement d'affiche</SelectItem>
                <SelectItem value="Changement d'image">Changement d'image</SelectItem>
                <SelectItem value="Nettoyage">Nettoyage</SelectItem>
                <SelectItem value="Mise à jour des logiciels">Mise à jour des logiciels</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un site ou une adresse…" className="pl-9 h-10 bg-slate-50 border-slate-200" data-testid="filter-search" />
            </div>
          </div>

          {/* Liste rows */}
          <div className="divide-y divide-slate-100 max-h-[720px] overflow-y-auto">
            {adminRows.length === 0 && (
              <div className="p-10 text-center text-sm text-slate-400">Aucune intervention pour ce filtre.</div>
            )}
            {adminRows.map((i, idx) => {
              const prio = i.priorite || 'Moyenne';
              const prioStyle = prio === 'Haute' ? 'bg-red-100 text-red-700' : prio === 'Basse' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700';
              const isEnCours = i.statut_intervention === 'En cours';
              const isDone = i.statut_intervention === 'Effectué';
              const isValidating = i.statut_intervention === 'En attente validation';
              const shortCode = `INT-${(i.date || '').slice(0, 4)}-${String(idx + 87).padStart(4, '0')}`;
              return (
                <div key={i.id}
                  onClick={() => setSelectedId(i.id)}
                  className="grid grid-cols-[auto_88px_1fr_auto_auto_auto] items-center gap-4 px-4 py-3.5 hover:bg-slate-50 cursor-pointer transition"
                  data-testid={`admin-row-${i.id}`}
                >
                  <Checkbox
                    onClick={(e) => e.stopPropagation()}
                    checked={selectedIds.has(i.id)}
                    onCheckedChange={(v) => { const s = new Set(selectedIds); if (v) s.add(i.id); else s.delete(i.id); setSelectedIds(s); }}
                  />
                  <div className="w-[88px] h-[52px] rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
                    <Monitor className="h-7 w-7 text-slate-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-mono text-slate-500 leading-none">{shortCode}</div>
                    <div className="font-bold text-slate-900 text-sm mt-0.5 truncate">
                      {i.lieu || '—'}{i.panneau?.numero ? ` — Panneau #${i.panneau.numero}` : ''}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                      <MapPin className="h-3 w-3" />
                      <span className="truncate">{i.lieu || '—'}{i.panneau?.ville ? `, ${i.panneau.ville}` : ''}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                      <Settings className="h-3 w-3" />
                      <span className="truncate">{i.motif || '—'}</span>
                    </div>
                    <span className={`inline-block mt-1.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${tacheBadgeClass(i.motif)}`}>
                      {i.motif || '—'}
                    </span>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <div className="flex items-center gap-1 text-sm text-slate-600 whitespace-nowrap">
                      <Calendar className="h-3.5 w-3.5 text-slate-400" /> {i.heure || '—'}
                    </div>
                    <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1 ${prioStyle}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${prio === 'Haute' ? 'bg-red-500' : prio === 'Basse' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                      {prio}
                    </span>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    {isDone ? (
                      <div className="h-9 px-4 rounded-md bg-emerald-50 text-emerald-700 text-xs font-semibold flex items-center gap-1.5">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Terminée
                      </div>
                    ) : isValidating ? (
                      <Button size="sm" onClick={() => setValidateTask(i)} className="h-9 bg-purple-600 hover:bg-purple-700 text-white" data-testid={`btn-valider-${i.id}`}>
                        À valider
                      </Button>
                    ) : isEnCours ? (
                      <Button size="sm" onClick={() => setRealizeTask(i)} className="h-9 bg-blue-600 hover:bg-blue-700 text-white" data-testid={`btn-encours-${i.id}`}>
                        En cours
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setRealizeTask(i)} className="h-9" data-testid={`btn-afaire-${i.id}`}>
                        À faire
                      </Button>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </div>
              );
            })}
          </div>
        </Card>

        {/* ══════ Sidebar droite ══════ */}
        <div className="space-y-4">
          {/* Synchroniser */}
          <div>
            <Button onClick={syncNow} className="w-full bg-blue-600 hover:bg-blue-700 gap-2 h-11" data-testid="btn-sync">
              <RefreshCw className="h-4 w-4" /> Synchroniser
            </Button>
            <p className="text-[11px] text-slate-500 mt-1.5 text-right">
              Dernière mise à jour : {new Date().toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>

          {/* Prochaine intervention */}
          <Card className="border border-slate-200 shadow-sm">
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                  <MapPin className="h-4 w-4 text-emerald-600" /> Prochaine intervention
                </div>
                {prochaineIntervention && (
                  <button onClick={openGoogleMapsRoute} className="text-[11px] text-blue-600 font-semibold hover:underline flex items-center gap-1" data-testid="btn-voir-itineraire">
                    <MapPin className="h-3 w-3" /> Voir l'itinéraire
                  </button>
                )}
              </div>
              {prochaineIntervention ? (
                <div className="space-y-2">
                  <div className="w-full h-24 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center">
                    <Monitor className="h-10 w-10 text-slate-400" />
                  </div>
                  <div className="font-bold text-slate-900 text-sm">
                    {prochaineIntervention.lieu || '—'}{prochaineIntervention.panneau?.numero ? ` — Panneau #${prochaineIntervention.panneau.numero}` : ''}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-600"><MapPin className="h-3 w-3 text-slate-400" />{prochaineIntervention.lieu || '—'}</div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-600"><Settings className="h-3 w-3 text-slate-400" />{prochaineIntervention.motif || '—'}</div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-600"><Calendar className="h-3 w-3 text-slate-400" />Aujourd'hui à {prochaineIntervention.heure || '—'}</div>
                  {prochaineIntervention.priorite === 'Haute' && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Haute priorité
                    </span>
                  )}
                  <Button onClick={() => setRealizeTask(prochaineIntervention)} className="w-full mt-2 bg-blue-600 hover:bg-blue-700 h-10 gap-2" data-testid="btn-demarrer-prochaine">
                    <Zap className="h-4 w-4" /> Démarrer l'intervention
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-slate-400 py-4 text-center italic">Aucune intervention à venir aujourd'hui.</p>
              )}
            </div>
          </Card>

          {/* Itinéraire du jour — carte Bangui */}
          <Card className="border border-slate-200 shadow-sm">
            <div className="p-4">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 mb-2">
                <MapPin className="h-4 w-4 text-blue-600" /> Mon itinéraire du jour ({adminRows.length} intervention{adminRows.length > 1 ? 's' : ''})
              </div>
              <div className="relative rounded-lg overflow-hidden bg-slate-100 border border-slate-200 aspect-video">
                <iframe
                  title="Carte Bangui"
                  loading="lazy"
                  className="w-full h-full"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=18.4%2C4.3%2C18.7%2C4.45&layer=mapnik"
                />
                {/* Overlay pins numérotés */}
                <div className="absolute inset-0 pointer-events-none">
                  {adminRows.slice(0, 6).map((i, idx) => {
                    const emp = empByName.get(i.lieu);
                    if (!emp || !emp.latitude || !emp.longitude) return null;
                    const bboxW = 0.3, bboxH = 0.15;
                    const x = ((Number(emp.longitude) - 18.4) / bboxW) * 100;
                    const y = ((4.45 - Number(emp.latitude)) / bboxH) * 100;
                    if (x < 0 || x > 100 || y < 0 || y > 100) return null;
                    return (
                      <div key={i.id} className="absolute -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center shadow-lg border-2 border-white"
                        style={{ left: `${x}%`, top: `${y}%` }}>
                        {idx + 1}
                      </div>
                    );
                  })}
                </div>
              </div>
              <Button variant="outline" onClick={openGoogleMapsRoute} className="w-full mt-3 gap-2 h-10" data-testid="btn-google-maps">
                <MapPin className="h-4 w-4" /> Voir sur Google Maps
              </Button>
            </div>
          </Card>

          {/* Rappels pour le terrain */}
          <Card className="border border-emerald-200 bg-emerald-50/40 shadow-sm">
            <div className="p-4">
              <div className="flex items-center gap-1.5 text-sm font-bold text-emerald-800 mb-3">
                <ClipboardList className="h-4 w-4 text-emerald-600" /> Rappels pour le terrain
              </div>
              <ul className="space-y-2 text-xs text-slate-700">
                {[
                  'Vérifier le matériel avant chaque intervention',
                  'Prendre une photo après chaque tâche',
                  'Signaler tout problème rencontré',
                  'Mettre à jour le statut dans le CRM',
                  'Synchroniser vos interventions en fin de journée',
                ].map((r, k) => (
                  <li key={k} className="flex items-start gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        </div>
      </div>


      {/* Panneau de détails supprimé de adminView → maintenant dans sharedModals */}
    </div>
  );

  // Modals + Sheets partagés entre les 2 vues
  const sharedModals = (
    <>
      {/* Panneau de détails d'intervention (partagé admin + tech) */}
      {selected && (
        <InterventionDetail
          intervention={selected}
          emplacement={selectedEmp}
          onClose={() => setSelectedId(null)}
          onReload={loadAll}
          isTechnicien={isTechnicien}
          onRealize={(t) => { setSelectedId(null); setRealizeTask(t); }}
          onReport={(t) => { setSelectedId(null); setReportTask(t); }}
        />
      )}
      {/* Sheet : Programmer une tâche technique */}
      <Sheet open={showProgramSheet} onOpenChange={setShowProgramSheet}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-6 bg-slate-50" data-testid="program-tache-sheet">
          <SheetHeader className="mb-4">
            <SheetTitle className="flex items-center gap-2 text-xl">
              <ClipboardList className="h-5 w-5 text-blue-600" />
              Programmer une tâche technique
            </SheetTitle>
            <SheetDescription>Renseignez toutes les informations de l'intervention à planifier.</SheetDescription>
          </SheetHeader>
          <ProgramTacheForm
            emplacements={emplacements}
            onClose={() => setShowProgramSheet(false)}
            onCreated={() => { setShowProgramSheet(false); loadAll(); }}
          />
        </SheetContent>
      </Sheet>

      {/* Dialog : Tâches automatiques */}
      <Dialog open={showAutoDialog} onOpenChange={setShowAutoDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-500" />
              Tâches automatiques
            </DialogTitle>
            <DialogDescription>
              Planification automatique des interventions récurrentes sur vos panneaux.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="p-3 rounded-lg border border-slate-200 flex items-start gap-3 bg-slate-50">
              <RefreshCw className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">Entretien mensuel</div>
                <div className="text-xs text-slate-500">Nettoyage complet des 14 panneaux tous les 1ers du mois.</div>
              </div>
              <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">ACTIF</span>
            </div>
            <div className="p-3 rounded-lg border border-slate-200 flex items-start gap-3 bg-slate-50">
              <Camera className="h-5 w-5 text-violet-600 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">Inspection trimestrielle</div>
                <div className="text-xs text-slate-500">Contrôle visuel + photo tous les 3 mois par panneau.</div>
              </div>
              <span className="text-[10px] font-bold text-slate-500 bg-slate-200 px-2 py-0.5 rounded">INACTIF</span>
            </div>
            <div className="p-3 rounded-lg border border-slate-200 flex items-start gap-3 bg-slate-50">
              <Zap className="h-5 w-5 text-amber-600 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">Vérification LED hebdo</div>
                <div className="text-xs text-slate-500">Diagnostic des 3 panneaux LED chaque lundi matin.</div>
              </div>
              <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">ACTIF</span>
            </div>
            <p className="text-xs text-slate-400 italic mt-2">
              Les tâches automatiques créent chaque exécution une intervention "Prévu" dans la liste ci-dessus.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal : Réaliser la tâche (technicien) */}
      {realizeTask && (
        <RealizeTaskModal
          task={realizeTask}
          onClose={() => setRealizeTask(null)}
          onDone={() => { setRealizeTask(null); loadAll(); }}
        />
      )}

      {/* Modal : Valider une tâche (admin) */}
      {validateTask && (
        <ValidateTaskModal
          task={validateTask}
          onClose={() => setValidateTask(null)}
          onDone={() => { setValidateTask(null); loadAll(); }}
        />
      )}

      {/* Modal : Reporter une tâche */}
      {reportTask && (
        <ReportTaskModal
          task={reportTask}
          onClose={() => setReportTask(null)}
          onDone={() => { setReportTask(null); loadAll(); }}
        />
      )}
    </>
  );

  return (
    <Layout user={user} onLogout={onLogout} currentPage="technique">
      {isTechnicien ? techView : adminView}
      {sharedModals}
    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────
// Panneau de détails à droite
// ─────────────────────────────────────────────────────────────
function InterventionDetail({ intervention: i, emplacement, onClose, onReload, isTechnicien, onRealize, onReport }) {
  const [photos, setPhotos] = useState({ avant: '', apres: '' });
  const [comment, setComment] = useState('');
  const [signature, setSignature] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/technique/interventions/${i.id}/photos`).then((r) => {
      setPhotos({ avant: r.data?.photo_avant || '', apres: r.data?.photo_apres || '' });
    }).catch(() => setPhotos({ avant: '', apres: '' }));
    setComment(i.description || i.motif || '');
    setSignature('');
    // eslint-disable-next-line
  }, [i.id]);

  const uploadPhoto = (which) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPhotos((p) => ({ ...p, [which]: ev.target?.result || '' }));
    reader.readAsDataURL(file);
  };

  const saveIntervention = async () => {
    try {
      await axios.post(`${API}/api/technique/interventions/${i.id}/marquer-effectue`, {
        photo_avant: photos.avant || undefined,
        photo_apres: photos.apres || undefined,
        commentaire: comment,
        signature,
      });
      toast.success('Intervention mise à jour');
      onReload?.();
    } catch { toast.error('Erreur de mise à jour'); }
  };

  const statut = STATUT_STYLE[i.statut_intervention] || STATUT_STYLE['Prévu'];

  return (
    <div className="fixed right-0 top-0 h-full w-full sm:w-[440px] bg-white border-l shadow-2xl z-40 overflow-y-auto" data-testid="intervention-detail-panel">
      <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between z-10">
        <h3 className="font-bold text-slate-900">Détails de l&apos;intervention</h3>
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-intervention-detail"><X className="h-4 w-4" /></Button>
      </div>

      <div className="p-5 space-y-5">
        {/* Actions rapides technicien pour tâche Prévue */}
        {isTechnicien && i.statut_intervention === 'Prévu' && (
          <div className="flex gap-2 -mt-2">
            <Button onClick={() => onRealize?.(i)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-2 h-10" data-testid="detail-tech-effectue">
              <CheckCircle2 className="h-4 w-4" /> Déjà effectué
            </Button>
            <Button onClick={() => onReport?.(i)} className="flex-1 bg-red-600 hover:bg-red-700 gap-2 h-10" data-testid="detail-tech-reporter">
              <X className="h-4 w-4" /> Reporter
            </Button>
          </div>
        )}
        <div className="flex items-center gap-3">
          <EmplacementBadge number={emplacement?.numero} type={emplacement?.type_panneau} />
          <div className="min-w-0">
            <div className="font-bold text-slate-900 truncate">{i.lieu || i.panneau?.emplacement || '—'}</div>
            <div className="text-[12px] text-slate-500 truncate">{emplacement?.type_panneau || i.panneau?.type || ''}</div>
          </div>
        </div>

        <div className="divide-y divide-slate-100 text-sm rounded-xl border border-slate-200">
          <div className="flex justify-between px-4 py-2"><span className="text-slate-600">Type d&apos;intervention</span><span className="font-medium text-slate-900">{i.type_intervention}</span></div>
          <div className="flex justify-between px-4 py-2"><span className="text-slate-600">Description</span><span className="font-medium text-slate-900 text-right ml-3 truncate max-w-[220px]">{i.description || i.motif}</span></div>
          <div className="flex justify-between px-4 py-2"><span className="text-slate-600">Date</span><span className="font-medium text-slate-900 capitalize">{formatDateFR(i.date)}</span></div>
          <div className="flex justify-between px-4 py-2"><span className="text-slate-600">Heure</span><span className="font-medium text-slate-900">{i.heure || '—'}</span></div>
          <div className="flex justify-between px-4 py-2"><span className="text-slate-600">Technicien</span><span className="font-medium text-slate-900">{i.technicien || '—'}</span></div>
          {typeof i.nombre_affiches === 'number' && i.nombre_affiches > 0 && (
            <div className="flex justify-between px-4 py-2"><span className="text-slate-600">Nb affiches à poser</span><span className="font-bold text-blue-700">{i.nombre_affiches}</span></div>
          )}
          {i.entreprises_affiches && (
            <div className="flex justify-between px-4 py-2 gap-3"><span className="text-slate-600 flex-shrink-0">Entreprise(s)</span><span className="font-medium text-slate-900 text-right break-words">{i.entreprises_affiches}</span></div>
          )}
          <div className="flex justify-between px-4 py-2"><span className="text-slate-600">Statut</span><span className={`inline-flex items-center gap-1.5 font-medium ${statut.bg}`}><span className={`w-1.5 h-1.5 rounded-full ${statut.dot}`} /> {statut.label}</span></div>
        </div>

        {/* Photos */}
        <div>
          <div className="text-sm font-semibold text-slate-900 mb-2">Justificatif d&apos;intervention</div>
          <div className="text-[11px] text-slate-500 mb-2">Photos prises</div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { key: 'avant', label: 'Avant', src: photos.avant },
              { key: 'apres', label: 'Après', src: photos.apres },
            ].map((p) => (
              <div key={p.key} className="aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                {p.src ? (
                  <img src={p.src} alt={p.label} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 text-[10px] gap-1">
                    <ImageIcon className="h-5 w-5" /> {p.label}
                  </div>
                )}
              </div>
            ))}
            <label className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-500 text-[10px] gap-1 cursor-pointer hover:border-blue-400 hover:text-blue-600">
              <Camera className="h-5 w-5" />
              Ajouter une photo
              <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto(photos.avant ? 'apres' : 'avant')} data-testid="add-photo-input" />
            </label>
          </div>
        </div>

        {/* Commentaire */}
        <div>
          <div className="text-sm font-semibold text-slate-900 mb-1">Commentaire du technicien</div>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Ex: Entretien effectué avec succès. Panneau propre et en bon état de fonctionnement." data-testid="intervention-comment" />
        </div>

        {/* Signature (si requis) */}
        <div>
          <div className="text-sm font-semibold text-slate-900 mb-1">Signature (si requis)</div>
          <div className="border border-slate-200 rounded-lg h-24 flex items-center justify-center bg-slate-50 relative">
            {signature ? (
              <img src={signature} alt="Signature" className="max-h-full max-w-full" />
            ) : (
              <span className="text-xs text-slate-400">Aucune signature</span>
            )}
            {signature && (
              <button onClick={() => setSignature('')} className="absolute top-1 right-1 p-1 rounded hover:bg-white" data-testid="clear-signature"><Trash2 className="h-3.5 w-3.5 text-rose-500" /></button>
            )}
          </div>
          <label className="inline-block mt-2 text-xs text-blue-600 cursor-pointer">
            + Ajouter une signature
            <input type="file" accept="image/*" className="hidden" onChange={(e) => {
              const f = e.target.files?.[0]; if (!f) return;
              const r = new FileReader(); r.onload = (ev) => setSignature(ev.target?.result || ''); r.readAsDataURL(f);
            }} data-testid="upload-signature-input" />
          </label>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <div className="text-xs uppercase font-semibold text-slate-500">Actions</div>
          {String(i.type_panneau || '').toLowerCase().includes('poteau') && (
            <Button
              variant="outline"
              className="w-full justify-center gap-2 border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800"
              onClick={async () => {
                try {
                  const r = await axios.get(
                    `${API}/api/technique/interventions/${i.id}/tournee-pdf`,
                    { responseType: 'blob' }
                  );
                  const url = URL.createObjectURL(r.data);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `Plan_tournee_${(i.poteau_avenue || i.lieu || 'avenue').replace(/\s+/g, '_')}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Erreur téléchargement plan de tournée');
                }
              }}
              data-testid="download-tournee-pdf"
            >
              <FileDown className="h-4 w-4" /> Télécharger plan de tournée ({i.poteau_count || 25} poteaux)
            </Button>
          )}
          <Button variant="outline" className="w-full justify-center gap-2" onClick={() => window.print()} data-testid="print-mission-order">
            <Printer className="h-4 w-4" /> Imprimer cet ordre de mission
          </Button>
          <Button
            variant="outline"
            className="w-full justify-center gap-2"
            onClick={() => (window.location.href = '/emplacement')}
            data-testid="view-emplacement-map"
          >
            <MapPin className="h-4 w-4" /> Voir l&apos;emplacement sur la carte
          </Button>
          <Button className="w-full bg-blue-600 hover:bg-blue-700 h-11" onClick={saveIntervention} data-testid="save-intervention">
            Modifier l&apos;intervention
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Sheet : Programmer une tâche technique (maquette officielle)
// ─────────────────────────────────────────────────────────────
const TYPES_TACHES = [
  'Maintenance',
  'Changement de toile',
  'Changement d\'affiche',
  'Changement d\'image',
  'Nettoyage',
  'Mise à jour des logiciels',
  'Autres tâches',
];

const TYPES_PANNEAUX = [
  { key: 'Panneau déroulant', label: 'Panneaux déroulants' },
  { key: 'Panneau LED',       label: 'Panneaux LED' },
  { key: 'Grand format',      label: 'Panneau grand format' },
];

function ProgramTacheForm({ emplacements, onClose, onCreated }) {
  const [form, setForm] = useState({
    date: todayISO(),
    heure: '10:00',
    technicien: '',
    client: '',
    notes: '',
    types_taches: [],  // multiple checkboxes
    type_panneau: 'Panneau déroulant',
    // Multi-sélection : { [emplacement_id]: { selected: bool, nb_affiches: number } }
    emplacements_selection: {},
    description: '',
  });
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/users`).then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
    axios.get(`${API}/api/clients`).then((r) => setClients(r.data || [])).catch(() => setClients([]));
  }, []);

  // Emplacements filtrés par type (tri par numéro)
  const empsFiltered = useMemo(
    () => (emplacements || [])
      .filter(e => e.type_panneau === form.type_panneau)
      .sort((a, b) => (Number(a.numero) || 0) - (Number(b.numero) || 0)),
    [emplacements, form.type_panneau]
  );

  // Reset la sélection quand on change de type de panneau
  useEffect(() => {
    setForm(prev => ({ ...prev, emplacements_selection: {} }));
  }, [form.type_panneau]);

  const selectedEmpIds = useMemo(
    () => Object.keys(form.emplacements_selection).filter(id => form.emplacements_selection[id]?.selected),
    [form.emplacements_selection]
  );

  const toggleEmplacement = (empId) => {
    setForm(prev => {
      const current = prev.emplacements_selection[empId];
      return {
        ...prev,
        emplacements_selection: {
          ...prev.emplacements_selection,
          [empId]: current?.selected
            ? { selected: false, nb_affiches: current.nb_affiches || 1, entreprises: current.entreprises || '' }
            : { selected: true,  nb_affiches: current?.nb_affiches || 1, entreprises: current?.entreprises || '' },
        },
      };
    });
  };

  const setNbAffiches = (empId, nb) => {
    setForm(prev => ({
      ...prev,
      emplacements_selection: {
        ...prev.emplacements_selection,
        [empId]: { ...(prev.emplacements_selection[empId] || {}), selected: true, nb_affiches: Math.max(0, Number(nb) || 0) },
      },
    }));
  };

  const setEntreprises = (empId, val) => {
    setForm(prev => ({
      ...prev,
      emplacements_selection: {
        ...prev.emplacements_selection,
        [empId]: { ...(prev.emplacements_selection[empId] || { selected: true, nb_affiches: 1 }), entreprises: val },
      },
    }));
  };

  const toggleTache = (t) => {
    setForm(prev => ({
      ...prev,
      types_taches: prev.types_taches.includes(t)
        ? prev.types_taches.filter(x => x !== t)
        : [...prev.types_taches, t],
    }));
  };

  const handleSave = async () => {
    if (!form.date || !form.heure) { toast.error('Date et heure obligatoires'); return; }
    if (!form.technicien) { toast.error("Choisir un technicien"); return; }
    if (form.types_taches.length === 0) { toast.error('Sélectionnez au moins un type de tâche'); return; }
    if (selectedEmpIds.length === 0) { toast.error('Sélectionnez au moins un emplacement'); return; }

    setSubmitting(true);
    try {
      // Crée une intervention par emplacement sélectionné (chaque tech peut gérer les affiches individuellement)
      const payloads = selectedEmpIds.map(empId => {
        const emp = empsFiltered.find(e => e.id === empId);
        const sel = form.emplacements_selection[empId] || {};
        const nb = sel.nb_affiches || 0;
        const entreprises = (sel.entreprises || '').trim();
        return {
          date: form.date,
          heure: form.heure,
          technicien: form.technicien,
          client: form.client,
          notes: form.notes,
          types_taches: form.types_taches,
          motif: form.types_taches.join(', '),
          type_panneau: form.type_panneau,
          emplacement_nom: emp?.nom || '',
          panneau_id: emp?.id || '',
          panneau_numero: emp?.numero || '',
          nombre_affiches: nb,
          entreprises_affiches: entreprises,
          description: form.description,
        };
      });
      await Promise.all(payloads.map(p => axios.post(`${API}/api/technique/interventions`, p)));
      toast.success(`${payloads.length} intervention${payloads.length > 1 ? 's' : ''} programmée${payloads.length > 1 ? 's' : ''} ✓`);
      if (onCreated) onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Infos générales */}
      <section className="bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3">
          <FileText className="h-4 w-4 text-blue-600" />
          Informations générales
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-600">Date de l'intervention *</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9" data-testid="pt-date" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Heure *</Label>
            <Input type="time" value={form.heure} onChange={(e) => setForm({ ...form, heure: e.target.value })} className="h-9" data-testid="pt-heure" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Technicien *</Label>
            <Select value={form.technicien} onValueChange={(v) => setForm({ ...form, technicien: v })}>
              <SelectTrigger className="h-9" data-testid="pt-technicien"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
              <SelectContent>
                {users.filter(u => (u.role || '') === 'technicien' || u.role === 'admin').map(u => (
                  <SelectItem key={u.email} value={u.name || u.email}>{u.name || u.email}</SelectItem>
                ))}
                {users.length === 0 && <SelectItem value="Karim Benali">Karim Benali</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Client *</Label>
            <Select value={form.client} onValueChange={(v) => setForm({ ...form, client: v })}>
              <SelectTrigger className="h-9" data-testid="pt-client"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
              <SelectContent>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.entreprise || c.nom || c.id}>{c.entreprise || c.nom}</SelectItem>
                ))}
                {clients.length === 0 && <SelectItem value="—">Aucun client</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-slate-600">Notes (optionnel)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Ajouter des informations complémentaires…" data-testid="pt-notes" />
          </div>
        </div>
      </section>

      {/* Type de tâche */}
      <section className="bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3">
          <Settings className="h-4 w-4 text-blue-600" />
          Type de tâche
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {TYPES_TACHES.map(t => {
            const active = form.types_taches.includes(t);
            return (
              <label
                key={t}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer text-sm transition ${
                  active ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
                data-testid={`pt-tache-${t.replace(/\s/g, '-')}`}
              >
                <Checkbox checked={active} onCheckedChange={() => toggleTache(t)} />
                <span className={active ? 'font-semibold text-blue-700' : 'text-slate-700'}>{t}</span>
              </label>
            );
          })}
        </div>
      </section>

      {/* Panneau concerné */}
      <section className="bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3">
          <Monitor className="h-4 w-4 text-blue-600" />
          Panneau concerné
        </h3>
        <Label className="text-xs text-slate-600 mb-2 block">Type de panneau *</Label>
        <div className="flex flex-wrap gap-3 mb-3">
          {TYPES_PANNEAUX.map(p => (
            <label
              key={p.key}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border cursor-pointer text-sm ${
                form.type_panneau === p.key ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold' : 'border-slate-200 bg-white text-slate-700'
              }`}
              data-testid={`pt-typepanneau-${p.key.replace(/\s/g, '-')}`}
            >
              <input
                type="radio"
                name="type_panneau"
                value={p.key}
                checked={form.type_panneau === p.key}
                onChange={() => setForm({ ...form, type_panneau: p.key, emplacement_id: '' })}
                className="h-3.5 w-3.5"
              />
              {p.label}
            </label>
          ))}
        </div>
        <div className="space-y-3">
          {/* Liste multi-sélection des emplacements */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-slate-600">Emplacements à inclure * ({selectedEmpIds.length}/{empsFiltered.length})</Label>
              <button
                type="button"
                onClick={() => {
                  if (selectedEmpIds.length === empsFiltered.length) {
                    setForm(prev => ({ ...prev, emplacements_selection: {} }));
                  } else {
                    const all = {};
                    empsFiltered.forEach(e => { all[e.id] = { selected: true, nb_affiches: form.emplacements_selection[e.id]?.nb_affiches || 1 }; });
                    setForm(prev => ({ ...prev, emplacements_selection: all }));
                  }
                }}
                className="text-[11px] text-blue-600 font-semibold hover:underline"
                data-testid="pt-toggle-all-emps"
              >
                {selectedEmpIds.length === empsFiltered.length ? 'Tout désélectionner' : 'Tout sélectionner'}
              </button>
            </div>
            {empsFiltered.length === 0 && (
              <div className="text-xs text-slate-400 italic p-3 border border-dashed rounded-lg">Aucun emplacement disponible pour ce type de panneau.</div>
            )}
            <div className="space-y-1.5 max-h-72 overflow-y-auto border border-slate-200 rounded-lg p-2 bg-slate-50">
              {empsFiltered.map((emp, idx) => {
                const state = form.emplacements_selection[emp.id];
                const isSel = !!state?.selected;
                return (
                  <div key={emp.id}
                    className={`rounded-md px-2.5 py-2 border transition ${
                      isSel ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`pt-emp-${emp.id}`}
                  >
                    <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2">
                      <Checkbox checked={isSel} onCheckedChange={() => toggleEmplacement(emp.id)} />
                      <div className="min-w-0 cursor-pointer" onClick={() => toggleEmplacement(emp.id)}>
                        <div className="text-sm font-medium text-slate-800 truncate">
                          <span className="inline-block bg-slate-200 text-slate-700 text-[10px] font-bold px-1.5 py-0.5 rounded mr-1.5">#{emp.numero || idx + 1}</span>
                          {emp.nom}
                        </div>
                        {emp.ville && <div className="text-[11px] text-slate-500 truncate">{emp.ville}</div>}
                      </div>
                      {isSel && (
                        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <label className="text-[10px] text-slate-500 whitespace-nowrap">Nb affiches</label>
                          <Input
                            type="number"
                            min="0"
                            max="20"
                            value={state?.nb_affiches ?? 1}
                            onChange={(e) => setNbAffiches(emp.id, e.target.value)}
                            className="h-8 w-16 text-center text-sm"
                            data-testid={`pt-emp-nb-${emp.id}`}
                          />
                        </div>
                      )}
                    </div>
                    {isSel && (
                      <div className="mt-2 pl-7" onClick={(e) => e.stopPropagation()}>
                        <label className="text-[10px] text-slate-500 block mb-1">Entreprise(s) des affiches</label>
                        <Input
                          type="text"
                          value={state?.entreprises || ''}
                          onChange={(e) => setEntreprises(emp.id, e.target.value)}
                          placeholder="Ex. Coca-Cola, Orange, Airtel…"
                          className="h-8 text-sm bg-white"
                          data-testid={`pt-emp-ent-${emp.id}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {selectedEmpIds.length > 0 && (
              <p className="text-[11px] text-slate-500 mt-1.5">
                → {selectedEmpIds.length} intervention{selectedEmpIds.length > 1 ? 's' : ''} sera{selectedEmpIds.length > 1 ? 'ont' : ''} créée{selectedEmpIds.length > 1 ? 's' : ''} (une par emplacement).
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs text-slate-600">Description de l'intervention (optionnel)</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Détails communs à toutes les interventions…" data-testid="pt-description" />
          </div>
        </div>
      </section>

      {/* Actions */}
      <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-white border-t border-slate-200 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={submitting}>Annuler</Button>
        <Button onClick={handleSave} disabled={submitting} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="pt-save">
          <Calendar className="h-4 w-4" />
          {submitting ? 'Enregistrement…' : 'Enregistrer le rendez-vous'}
        </Button>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────
// Modal : Réaliser la tâche (technicien uniquement)
// ─────────────────────────────────────────────────────────────
function RealizeTaskModal({ task, onClose, onDone }) {
  const [photoAvant, setPhotoAvant] = useState('');
  const [photoApres, setPhotoApres] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [stepsDone, setStepsDone] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const steps = task.types_taches && task.types_taches.length ? task.types_taches : [task.motif || 'Réaliser la tâche'];

  const handleFile = (setter) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Photo trop volumineuse (max 8 Mo)'); return; }
    const reader = new FileReader();
    reader.onload = () => setter(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!photoAvant || !photoApres) {
      toast.error("2 photos obligatoires : arrivée + fin d'intervention");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/technique/interventions/${task.id}/marquer-effectue`, {
        photo_avant: photoAvant,
        photo_apres: photoApres,
        commentaire,
      });
      toast.success("Envoyée pour validation ✓");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[92vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-bold text-slate-900">Marquer comme effectuée</h3>
            <p className="text-xs text-slate-500">{task.motif} · {task.lieu}</p>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <p className="text-xs font-semibold text-slate-700">Étapes réalisées</p>
          {steps.map((s, i) => (
            <label key={i} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={!!stepsDone[i]} onCheckedChange={(v) => setStepsDone({ ...stepsDone, [i]: v })} />
              <span className={stepsDone[i] ? 'line-through text-slate-400' : 'text-slate-800'}>{s}</span>
            </label>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <PhotoSlot label="📸 Arrivée" required value={photoAvant} onChange={handleFile(setPhotoAvant)} testid="upload-photo-avant" />
          <PhotoSlot label="📸 Fin" required value={photoApres} onChange={handleFile(setPhotoApres)} testid="upload-photo-apres" />
        </div>
        <p className="text-[10px] text-slate-500 mt-1">Les 2 photos servent de preuve de présence sur le panneau (arrivée puis fin de tâche).</p>

        <div className="mt-4">
          <p className="text-xs font-semibold text-slate-700 mb-1">Commentaire (optionnel)</p>
          <Textarea rows={3} value={commentaire} onChange={(e) => setCommentaire(e.target.value)}
            placeholder="Ajouter un commentaire..." data-testid="commentaire" />
        </div>

        <div className="mt-5 flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting} className="flex-1">Annuler</Button>
          <Button onClick={submit} disabled={submitting || !photoAvant || !photoApres}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-2"
            data-testid="btn-envoyer-validation">
            <CheckCircle2 className="h-4 w-4" />
            {submitting ? 'Envoi…' : 'Envoyer pour validation'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PhotoSlot({ label, value, onChange, required, testid }) {
  return (
    <label
      className={`flex flex-col items-center justify-center rounded-lg cursor-pointer transition py-6 border-2 border-dashed ${
        value ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300 bg-slate-50 hover:bg-slate-100'
      }`}
      data-testid={testid}
    >
      {value ? (
        <img src={value} alt={label} className="max-h-24 rounded object-cover" />
      ) : (
        <>
          <Camera className="h-6 w-6 text-slate-500 mb-1" />
          <span className="text-[11px] font-semibold text-slate-700">{label}</span>
          {required && <span className="text-[9px] text-red-500">*obligatoire</span>}
        </>
      )}
      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onChange} />
    </label>
  );
}

// ─────────────────────────────────────────────────────────────
// Modal : Valider une tâche soumise (admin uniquement)
// ─────────────────────────────────────────────────────────────
function ValidateTaskModal({ task, onClose, onDone }) {
  const [photos, setPhotos] = useState(null);
  const [motif, setMotif] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/technique/interventions/${task.id}/photos`)
      .then(r => setPhotos(r.data))
      .catch(() => setPhotos({}));
  }, [task.id]);

  const decide = async (action) => {
    if (action === 'reject' && !motif.trim()) {
      toast.error("Précisez un motif de rejet");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/technique/interventions/${task.id}/validate`, { action, motif });
      toast.success(action === 'approve' ? 'Tâche validée ✓' : 'Tâche rejetée');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[92vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle2 className="h-5 w-5 text-purple-600" />
          <div>
            <h3 className="font-bold text-slate-900">Valider l'intervention</h3>
            <p className="text-xs text-slate-500">{task.motif} — {task.lieu} · Technicien : {task.technicien}</p>
          </div>
        </div>

        {photos === null ? (
          <div className="text-center py-8 text-sm text-slate-500">Chargement des preuves…</div>
        ) : (
          <>
            {photos.photo_apres ? (
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-2">Preuve photo :</p>
                <img src={photos.photo_apres} alt="preuve" className="w-full rounded-lg border" />
              </div>
            ) : photos.photo_avant ? (
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-2">Preuve photo :</p>
                <img src={photos.photo_avant} alt="preuve" className="w-full rounded-lg border" />
              </div>
            ) : (
              <p className="text-sm text-red-600">⚠️ Aucune photo fournie</p>
            )}

            {photos.commentaire && (
              <div className="mt-3 p-3 bg-slate-50 rounded text-sm text-slate-700">
                <span className="font-semibold text-slate-500 text-xs">Commentaire technicien : </span>
                {photos.commentaire}
              </div>
            )}

            <div className="mt-4">
              <p className="text-xs font-semibold text-slate-700 mb-1">Motif de rejet (si applicable)</p>
              <Textarea rows={2} value={motif} onChange={(e) => setMotif(e.target.value)}
                placeholder="Photo floue, tâche incomplète..." data-testid="motif-rejet" />
            </div>
          </>
        )}

        <div className="mt-5 flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting} className="flex-1">Annuler</Button>
          <Button onClick={() => decide('reject')} disabled={submitting}
            className="flex-1 bg-red-600 hover:bg-red-700 gap-2 text-white"
            data-testid="btn-reject">
            <X className="h-4 w-4" /> Rejeter
          </Button>
          <Button onClick={() => decide('approve')} disabled={submitting}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-2"
            data-testid="btn-approve">
            <CheckCircle2 className="h-4 w-4" /> Valider
          </Button>
        </div>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────
// Modal : Reporter une tâche (avec explication écrite)
// ─────────────────────────────────────────────────────────────
function ReportTaskModal({ task, onClose, onDone }) {
  const [motif, setMotif] = useState('');
  const [nouvelleDate, setNouvelleDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!motif.trim()) { toast.error("Écrivez une explication pour reporter"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/technique/interventions/${task.id}/reporter`, {
        motif_report: motif.trim(),
        nouvelle_date: nouvelleDate || undefined,
      });
      toast.success(nouvelleDate ? "Reportée à la nouvelle date ✓" : "Tâche reportée ✓");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-9 h-9 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
            <X className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-bold text-slate-900">Reporter la tâche</h3>
            <p className="text-xs text-slate-500">{task.motif} · {task.lieu}</p>
          </div>
        </div>

        <div className="mt-4">
          <p className="text-xs font-semibold text-slate-700 mb-1">Explication <span className="text-red-500">(obligatoire)</span></p>
          <Textarea rows={4} value={motif} onChange={(e) => setMotif(e.target.value)}
            placeholder="Pourquoi cette tâche est-elle reportée ? (météo, matériel manquant, absence, …)"
            data-testid="motif-report" />
        </div>

        <div className="mt-3">
          <p className="text-xs font-semibold text-slate-700 mb-1">Nouvelle date (optionnel)</p>
          <input type="date" value={nouvelleDate} onChange={(e) => setNouvelleDate(e.target.value)}
            className="w-full h-9 px-2 rounded border border-slate-300 text-sm" data-testid="nouvelle-date" />
          <p className="text-[10px] text-slate-500 mt-1">Si renseigné, la tâche est replanifiée à cette date.</p>
        </div>

        <div className="mt-5 flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting} className="flex-1">Annuler</Button>
          <Button onClick={submit} disabled={submitting || !motif.trim()}
            className="flex-1 bg-red-600 hover:bg-red-700 gap-2"
            data-testid="btn-confirm-report">
            <X className="h-4 w-4" />
            {submitting ? 'Envoi…' : 'Confirmer le report'}
          </Button>
        </div>
      </div>
    </div>
  );
}

